import React, { useState } from 'react';
import { Alert, Button, Input, Space, Upload, message } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { replyNonStd, uploadNonStdAttachment } from '../../services/nonstandardOps';
import { describeUploadError, ensureUploadSize } from '../../utils/uploadLimits';
import { NonStdAttachment } from '../../types/nonstandardOps';

interface Props {
  instructionId: number;
  /** 正在等客户反馈：醒目提示 */
  waiting: boolean;
  onReplied: () => void;
}

/** 客户回复：文字 + 附件（比如每个 LPN 的处理方式表格）。回复后仓库和管理员都能看到。 */
const CustomerReplyForm: React.FC<Props> = ({ instructionId, waiting, onReplied }) => {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<NonStdAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const upload = async (file: File) => {
    setUploading(true);
    try {
      if (!ensureUploadSize(file)) return false;
      const uploaded = await uploadNonStdAttachment(file, instructionId);
      setAttachments(prev => [...prev, uploaded]);
    } catch (err: any) {
      message.error(`“${file.name}”上传失败：${describeUploadError(err)}`);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const submit = async () => {
    if (!text.trim() && attachments.length === 0) {
      message.warning('请填写回复内容，或上传附件');
      return;
    }
    setSaving(true);
    try {
      await replyNonStd(instructionId, { text: text.trim(), attachments });
      message.success('已回复，仓库会按您的回复继续处理');
      setText('');
      setAttachments([]);
      onReplied();
    } catch (err: any) {
      const data = err?.response?.data;
      message.error(data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || '回复失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 12, padding: 12, background: waiting ? '#fff7e6' : '#fafafa', borderRadius: 6, border: waiting ? '1px solid #ffd591' : undefined }}>
      {waiting ? (
        <Alert type="warning" showIcon style={{ marginBottom: 8 }} message="仓库已提交本轮结果，正在等待您的反馈。请查看上面的记录和附件后在这里回复。" />
      ) : null}
      <div style={{ fontWeight: 600, marginBottom: 8 }}>回复仓库</div>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Input.TextArea rows={3} maxLength={2000} placeholder="请反馈您的处理意见与需求，也可以上传多种附件文件，如新的操作视频/照片/LPN对应新条码列表文件等" value={text} onChange={e => setText(e.target.value)} />
        <Space wrap>
          <Upload multiple showUploadList={false} beforeUpload={upload as any} disabled={uploading || attachments.length >= 20}>
            <Button icon={<PaperClipOutlined />} loading={uploading}>上传附件（表格/文档/图片/视频）</Button>
          </Upload>
          {attachments.map((att, index) => (
            <span key={att.url} style={{ fontSize: 12 }}>
              {att.name || att.url.split('/').pop()}
              <a style={{ marginLeft: 4 }} onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}>移除</a>
            </span>
          ))}
          <Button type="primary" loading={saving} onClick={submit}>提交回复</Button>
        </Space>
      </Space>
    </div>
  );
};

export default CustomerReplyForm;
